import i18n from "lib/i18n";
import PropTypes from "prop-types";
import React, { useState } from "react";
import getSharedClassNameGenerator from "lib/shared_class_name_generator";
import { ThemeProvider } from "@theconversation/ui";
import { track } from "lib/analytics";

import SubmitForm from "./SubmitForm";
import ToggleLockButton from "../../../components/Editorial/ToggleLockUser/Button";
import ToggleLockDialog from "../../../components/Editorial/ToggleLockUser/Dialog";

/**
 * Component that will render a button to lock/unlock users. Clicking
 * on the button will then show a dialog to input the lock/unlock reason.
 *
 * @param {Function} [afterSubmit] callback function executed after the lock/unlock submit request
 * @param {String} buttonClass class name attribute for the button
 * @param {Boolean} locked user is locked or not
 * @param {{action: String, category: String, label: String}} trackAction event to be submitted to Google Analytics
 * @param {Number, String} userId the user ID
 * @param {String} userName the user name
 */
export default function ToggleLockUser({
  afterSubmit,
  buttonClass,
  locked,
  trackAction,
  userId,
  userName,
}) {
  const [userLocked, setUserLocked] = useState(locked);
  const [dialogOpen, setDialogOpen] = useState(false);

  const submitForm = (reason) => {
    new SubmitForm({
      locked: userLocked,
      reason,
      userId,
    }).submit((isLocked) => {
      afterSubmit(isLocked);
      setUserLocked(isLocked);
      setDialogOpen(false);
    });
  };

  const toggleLockText = userLocked ?
    i18n.t("editorial_toggle_lock_user.action.unlock") :
    i18n.t("editorial_toggle_lock_user.action.lock");

  return (
    <ThemeProvider generateClassName={getSharedClassNameGenerator()}>
      <ToggleLockButton
        className={buttonClass}
        locked={userLocked}
        onClick={() => {
          track(trackAction.category, trackAction.action, trackAction.label);
          setDialogOpen(true);
        }}
      >
        {toggleLockText}
      </ToggleLockButton>

      <ToggleLockDialog
        locked={userLocked}
        onCancel={() => setDialogOpen(false)}
        onSubmit={submitForm}
        open={dialogOpen}
        userId={userId}
        userName={userName}
      />
    </ThemeProvider>
  );
}

ToggleLockUser.defaultProps = {
  afterSubmit: () => {},
  buttonClass: "",
  trackAction: {
    action: "Click",
    category: "Editorial",
    label: "User Lock",
  },
};

ToggleLockUser.propTypes = {
  afterSubmit: PropTypes.func,
  buttonClass: PropTypes.string,
  locked: PropTypes.bool.isRequired,
  trackAction: PropTypes.shape({
    action: PropTypes.string.isRequired,
    category: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  }),
  userId: PropTypes.oneOfType([
    PropTypes.string, PropTypes.number,
  ]).isRequired,
  userName: PropTypes.string.isRequired,
};

